import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type LldpIf = { interface: number; tx: boolean };

function deepValue(obj: unknown, name: string): string {
  if (!obj || typeof obj !== 'object') return '';
  const rec = obj as Record<string, unknown>;
  if (typeof rec[name] === 'string' || typeof rec[name] === 'number')
    return String(rec[name]);

  Object.values(rec).forEach((v) => {
    const value = deepValue(v as Record<string, unknown>, name);
    if (value) return value;
    return '';
  });
  return '';
}

export default function WebUiSettingsLldpPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [ifs, setIfs] = React.useState<LldpIf[]>([]);
  const [neighbors, setNeighbors] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const loadInterfaces = React.useCallback(async () => {
    const data = await postWebUiApi<LldpIf[]>({
      service: 'lldp',
      access: 'get',
      command: 'get interfaces',
    });
    setIfs(data ?? []);
  }, []);

  const loadNeighbors = React.useCallback(async () => {
    const data = await postWebUiApi<any>({
      service: 'lldp',
      access: 'get',
      command: 'get neighbors',
    });
    const source = data?.lldp?.interface;
    setNeighbors(
      (() => {
        if (Array.isArray(source)) return source;
        if (source) return [source];
        return [];
      })().map((item) => ({
        interface: item.interface,
        data: item.data,
      })),
    );
  }, []);

  React.useEffect(() => {
    Promise.all([loadInterfaces(), loadNeighbors()]).catch((err) =>
      setError((err as Error).message),
    );
  }, [loadInterfaces, loadNeighbors]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'LLDP']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-row gap-2">
        <div className="flex flex-col gap-2">
          <h4 className="font-bold">Configuration</h4>
          <table className="webui-table min-w-52 max-w-fit">
            <thead>
              <tr className="webui-table-row-head">
                <th>Port</th>
                <th>Setting</th>
              </tr>
              {ifs.map((row) => (
                <tr key={row.interface} className="webui-table-row-content">
                  <td className="text-center">{row.interface}</td>
                  <td className="text-center">
                    <WebUiSelect
                      inputSize="sm"
                      className="w-full"
                      options={[
                        { label: 'receive only', value: 'false' },
                        { label: 'receive & transmit', value: 'true' },
                      ]}
                      disabled={readonly}
                      value={String(row.tx)}
                      onChange={(e) =>
                        setIfs((prev) =>
                          prev.map((x) =>
                            x.interface === row.interface
                              ? { ...x, tx: e.target.value === 'true' }
                              : x,
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </thead>
          </table>
          <div className="flex w-full gap-2">
            <WebUiButton
              disabled={readonly}
              onClick={async () => {
                try {
                  await postWebUiApi({
                    service: 'lldp',
                    access: 'set',
                    command: 'set interfaces',
                    parameters: ifs,
                  });
                  setMessage('LLDP interfaces updated.');
                  setError(null);
                  await loadInterfaces();
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              Set
            </WebUiButton>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <h4 className="font-bold">Neighbours</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Local Port</th>
                <th>System Name</th>
                <th>System Description</th>
                <th>Chassis ID Type</th>
                <th>Chassis ID Value</th>
                <th>Port ID Type</th>
                <th>Port ID Value</th>
                <th>Port Description</th>
                <th>Age</th>
              </tr>
              {neighbors.map((n, idx) => {
                const d = n?.data ?? {};
                return (
                  <tr key={idx} className="webui-table-row-content">
                    <td className="text-center">
                      {String(n?.interface ?? '')}
                    </td>
                    <td className="text-center">
                      {String(Object.keys(d?.chassis ?? {})[0] ?? '').replace(
                        /^id$/,
                        '',
                      )}
                    </td>
                    <td className="text-center">
                      {deepValue(d?.chassis, 'descr')}
                    </td>
                    <td className="text-center">
                      {deepValue(d?.chassis, 'type')}
                    </td>
                    <td className="text-center">
                      {deepValue(d?.chassis, 'value')}
                    </td>
                    <td className="text-center">
                      {String(d?.port?.id?.type ?? '')}
                    </td>
                    <td className="text-center">
                      {String(d?.port?.id?.value ?? '')}
                    </td>
                    <td className="text-center">
                      {deepValue(d?.port, 'descr')}
                    </td>
                    <td className="text-center">{String(d?.age ?? '')}</td>
                  </tr>
                );
              })}
            </thead>
          </table>
          <div className="flex w-full gap-2">
            <WebUiButton
              onClick={() =>
                loadNeighbors().catch((err) => setError((err as Error).message))
              }
            >
              Refresh
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
